import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Query } from '../op-types';

export function createCalendarQueries(client: StoxlyClient) {
  return createQueryKeys('calendar', {
    // ['calendar', 'dividends', { query }]
    dividends: (query: Query<'finnhubCalendarGetDividends'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.finnhubCalendarGetDividends({ queries: query, signal }),
    }),

    // ['calendar', 'dividendsBatch', { query }]
    dividendsBatch: (query: Query<'finnhubCalendarGetDividendsBatch'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.finnhubCalendarGetDividendsBatch({ queries: query, signal }),
    }),

    // ['calendar', 'earningsCalendar', { query }]
    earningsCalendar: (query: Query<'finnhubCalendarGetEarningsCalendar'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.finnhubCalendarGetEarningsCalendar({ queries: query, signal }),
    }),

    // ['calendar', 'earningsBatch', { query }]
    earningsBatch: (query: Query<'finnhubCalendarGetEarningsBatch'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.finnhubCalendarGetEarningsBatch({ queries: query, signal }),
    }),

    // ['calendar', 'earningsCallsLive', { query }]
    earningsCallsLive: (query: Query<'finnhubCalendarGetEarningsCallsLive'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.finnhubCalendarGetEarningsCallsLive({ queries: query, signal }),
    }),

    // ['calendar', 'earningsCallsBatch', { query }]
    earningsCallsBatch: (query: Query<'finnhubCalendarGetEarningsCallsBatch'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.finnhubCalendarGetEarningsCallsBatch({ queries: query, signal }),
    }),

    // ['calendar', 'economicCalendar', { query }]
    economicCalendar: (query: Query<'finnhubCalendarGetEconomicCalendar'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.finnhubCalendarGetEconomicCalendar({ queries: query, signal }),
    }),

    // ['calendar', 'marketHolidays', { query }]
    marketHolidays: (query: Query<'finnhubCalendarGetMarketHolidays'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.finnhubCalendarGetMarketHolidays({ queries: query, signal }),
    }),

    // ['calendar', 'stream', { query }] — fox F9. The five feeds above, merged, rated and paged
    // ONCE on the server. The eight ops above stay for the pre-fox calendar surfaces (the
    // portfolio and watchlist hubs still fan out); the research page rides this one.
    stream: (query: Query<'researchCalendarGetStream'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.researchCalendarGetStream({ queries: query, signal }),
    }),

    // ['calendar', 'portfolioEarnings', { query }]
    portfolioEarnings: (query: Query<'researchCalendarGetPortfolioEarnings'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.researchCalendarGetPortfolioEarnings({ queries: query, signal }),
    }),

    // ['calendar', 'upcoming', symbol, { query }]
    upcoming: (symbol: string, query: Query<'researchCalendarGetUpcoming'>) => ({
      queryKey: [symbol, { query }],
      queryFn: ({ signal }) =>
        client.researchCalendarGetUpcoming({ params: { symbol }, queries: query, signal }),
    }),

    // ['calendar', 'exportIcs', { query }] — fox F9. The window on screen as an
    // iCalendar document, under the SAME filters as `stream`.
    //
    // The BROWSER DOWNLOAD does not ride this definition: a file save needs a
    // `Blob`, which the validated ky aliases cannot return, so the button goes
    // through `download-client.ts` (the one sanctioned non-spine transport) —
    // the same split the portfolio/wallet/report CSV exports already live with.
    // The definition is here because the spine owns the operation inventory:
    // any non-download reader (a preview, a test, a future adapter) gets the
    // typed call from the same place as every other calendar read.
    exportIcs: (query: Query<'researchCalendarExportCalendar'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.researchCalendarExportCalendar({ queries: query, signal }),
    }),
  });
}
